import React, { FC, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { AdvancedSelect } from 'src/components/widgets/select';
import { OptionType as OptionWithSubLabelType, OptionWithSubLabel } from 'src/shared/globals';
import { stringFormat, T } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import { TOOL_DEFINITIONS } from '../common/constants';
import {
    FieldCaption,
    FormGroup,
    FormInput,
    FormLabel,
    FormSelect,
    SectionCard,
    SectionCardTitle,
    SettingHint,
    SharedWarningCallout,
    StackedBody,
    StackedBreadcrumb
} from '../common/styled';
import type { moqRuleEditor_MoqRuleDetailQuery } from './__generated__/moqRuleEditor_MoqRuleDetailQuery.graphql';
import type { moqRuleEditor_UpdateMoqRuleMutation } from './__generated__/moqRuleEditor_UpdateMoqRuleMutation.graphql';

export interface MoqRuleEditorProps {
    itemId: string;
    productId: string;
    toolKey: string;
    onBack: () => void;
    onSave: () => void;
    saveRef: React.MutableRefObject<(() => Promise<void>) | null>;
    onCanSaveChange?: (canSave: boolean) => void;
}

// FIXED step requires the minimum to be the step value (when below) or a multiple of it (when above).
// Anything else gets snapped to the nearest valid multiple — never below the step itself.
type MinAdjustment = { value: number; reason: 'below' | 'multiple' };

function computeFixedMinAdjustment(minStr: string, stepStr: string | null): MinAdjustment | null {
    const min = parseInt(minStr);
    const step = parseInt(stepStr ?? '');
    if (!min || !step || min < 1 || step < 1) return null;
    if (min < step) return { value: step, reason: 'below' };
    if (min % step !== 0) return { value: Math.max(step, Math.round(min / step) * step), reason: 'multiple' };
    return null;
}

const moqRuleDetailQuery = graphql`
    query moqRuleEditor_MoqRuleDetailQuery($id: ID!) {
        moqRule(id: $id) {
            id
            moqRuleID
            name
            minimumQuantity
            targetRuleType
            stepType
            fixedStep
            customTiers
            products {
                productID
            }
            printTypes {
                printTypeID
            }
            colors {
                colorID
                productID
            }
            globalColors
        }
    }
`;

const MoqRuleEditorContent: FC<MoqRuleEditorProps> = ({ itemId, productId, toolKey, onBack, onSave, saveRef, onCanSaveChange }) => {
    const toolName = TOOL_DEFINITIONS.find((tool) => tool.key === toolKey)?.name || '';
    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query moqRuleEditor_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const json = useLazyLoadQuery<moqRuleEditor_MoqRuleDetailQuery>(moqRuleDetailQuery, { id: itemId });
    const rule = json?.moqRule;
    const ruleName = rule?.name || '';
    const assignedProductCount = rule?.products?.length;

    const [minimumQuantity, setMinimumQuantity] = useState(() => String(rule?.minimumQuantity ?? 1));
    const [targetRuleType, setTargetRuleType] = useState<'PRODUCT' | 'VARIANT'>(() => rule?.targetRuleType || 'PRODUCT');
    const [stepType, setStepType] = useState(() => rule?.stepType || 'FIXED');
    const [fixedStep, setFixedStep] = useState(() => String(rule?.fixedStep ?? 1));
    const [customTiers, setCustomTiers] = useState(() => rule?.customTiers || '');
    // Note shown when we auto-snap the min to satisfy the FIXED step constraint.
    // Cleared on the next user edit to either field so it doesn't linger after the value is consistent.
    const [minAdjustment, setMinAdjustment] = useState<MinAdjustment | null>(null);

    const isMinValid = stepType === 'CUSTOM' || (Boolean(minimumQuantity) && parseInt(minimumQuantity) >= 1);
    const isFixedStepValid = stepType !== 'FIXED' || (Boolean(fixedStep) && parseInt(fixedStep) >= 1);
    const isCustomTiersValid =
        stepType !== 'CUSTOM' ||
        (customTiers.trim().length > 0 &&
            customTiers.split(',').every((v) => {
                const trimmed = v.trim();
                return trimmed.length > 0 && /^\d+$/.test(trimmed) && parseInt(trimmed, 10) > 0;
            }));
    const canSave = isMinValid && isFixedStepValid && isCustomTiersValid;

    useEffect(() => {
        onCanSaveChange?.(canSave);
    }, [canSave, onCanSaveChange]);

    const applyFixedMinAdjustment = () => {
        if (stepType !== 'FIXED') return;
        const result = computeFixedMinAdjustment(minimumQuantity, fixedStep);
        if (result) {
            setMinAdjustment(result);
            setMinimumQuantity(String(result.value));
        }
    };

    const targetRuleTypeOptions: OptionWithSubLabelType[] = useMemo(
        () => [
            {
                value: 'PRODUCT',
                label: T._('Product', 'Admin'),
                subLabel: T._('The minimum quantity applies to the total amount of the product, regardless of variants.', 'Admin')
            },
            {
                value: 'VARIANT',
                label: T._('Variant', 'Admin'),
                subLabel: T._('The minimum quantity applies independently to each variant of the product.', 'Admin')
            }
        ],
        []
    );
    const selectedTargetRuleTypeOption = targetRuleTypeOptions.find((o) => o.value === targetRuleType) ?? targetRuleTypeOptions[0];

    const [commitUpdateMoq] = useMutation<moqRuleEditor_UpdateMoqRuleMutation>(graphql`
        mutation moqRuleEditor_UpdateMoqRuleMutation($input: MoqRuleInput!) {
            updateMoqRule(input: $input) {
                moqRule {
                    id
                    name
                    minimumQuantity
                    targetRuleType
                    stepType
                    fixedStep
                    customTiers
                }
            }
        }
    `);

    const handleSave = useCallback(async () => {
        if (!rule) return;
        if (!canSave) return;
        try {
            await new Promise<void>((resolve, reject) => {
                commitUpdateMoq({
                    variables: {
                        input: {
                            id: rule.id,
                            name: rule.name,
                            // CUSTOM tiers don't expose a minimum to the user; persist 1 so the plugin keeps a sensible
                            // floor while the FE hides the field on read (mirrors moqRuleToInput in the MOQ tool page).
                            minimumQuantity: stepType === 'CUSTOM' ? 1 : parseInt(minimumQuantity) || 1,
                            targetRuleType: targetRuleType,
                            stepType: stepType,
                            fixedStep: stepType === 'FIXED' ? parseInt(fixedStep) || 1 : null,
                            customTiers: stepType === 'CUSTOM' ? customTiers : null,
                            products: rule.products.map((p) => p.productID),
                            printTypes: rule.printTypes.map((p) => p.printTypeID),
                            colors: rule.colors.map((c) => ({ colorID: c.colorID, productID: c.productID })),
                            globalColors: rule.globalColors as string[]
                        }
                    },
                    onCompleted: () => {
                        onSave();
                        resolve();
                    },
                    onError: (error) => reject(error)
                });
            });
        } catch {
            /* ignore */
        }
    }, [rule, canSave, minimumQuantity, targetRuleType, stepType, fixedStep, customTiers, onSave, commitUpdateMoq]);

    saveRef.current = handleSave;

    return (
        <>
            <StackedBreadcrumb>
                <span onClick={onBack}>{productName}</span> / {toolName} / {ruleName}
            </StackedBreadcrumb>
            <StackedBody>
                {assignedProductCount != null && assignedProductCount > 1 && (
                    <SharedWarningCallout>
                        {T._('This rule is assigned to', 'Admin')} <strong>{assignedProductCount}</strong>{' '}
                        {T._('products. Any changes will apply to all of them.', 'Admin')}
                    </SharedWarningCallout>
                )}
                <SectionCard>
                    <SectionCardTitle>{T._('Quantity Threshold', 'Admin')}</SectionCardTitle>

                    <FormGroup>
                        <FormLabel>{T._('Target rule type', 'Admin')}</FormLabel>
                        <AdvancedSelect<OptionWithSubLabelType>
                            options={targetRuleTypeOptions}
                            value={selectedTargetRuleTypeOption}
                            onChange={(val) => {
                                if (!val || 'length' in val) return;
                                setTargetRuleType(val.value as 'PRODUCT' | 'VARIANT');
                            }}
                            components={{ Option: OptionWithSubLabel }}
                            isSearchable={false}
                        />
                    </FormGroup>

                    {stepType !== 'CUSTOM' && (
                        <FormGroup>
                            <FormLabel>{T._('Minimum quantity per order', 'Admin')}</FormLabel>
                            <FormInput
                                type='number'
                                min={1}
                                value={minimumQuantity}
                                onChange={(e) => {
                                    setMinAdjustment(null);
                                    setMinimumQuantity(e.target.value);
                                }}
                                onBlur={applyFixedMinAdjustment}
                                $isError={!isMinValid}
                            />
                            {minAdjustment && (
                                <FieldCaption>
                                    {minAdjustment.reason === 'below'
                                        ? stringFormat(T._('Minimum was below the increment and has been raised to {0}.', 'Admin'), String(minAdjustment.value))
                                        : stringFormat(
                                              T._('Minimum was not a multiple of the increment and has been adjusted to {0}.', 'Admin'),
                                              String(minAdjustment.value)
                                          )}
                                </FieldCaption>
                            )}
                        </FormGroup>
                    )}
                </SectionCard>

                <SectionCard>
                    <SectionCardTitle>{T._('Quantity increments', 'Admin')}</SectionCardTitle>

                    <FormGroup>
                        <FormLabel>{T._('Increment type', 'Admin')}</FormLabel>
                        <FormSelect
                            value={stepType}
                            onChange={(e) => {
                                setMinAdjustment(null);
                                setStepType(e.target.value as 'FIXED' | 'CUSTOM');
                            }}
                        >
                            <option value='FIXED'>{T._('Fixed steps', 'Admin')}</option>
                            <option value='CUSTOM'>{T._('Custom tiers', 'Admin')}</option>
                        </FormSelect>
                    </FormGroup>

                    {stepType === 'FIXED' && (
                        <FormGroup>
                            <FormLabel>{T._('Step value', 'Admin')}</FormLabel>
                            <SettingHint>
                                {T._('Customers can only increase the quantity by the step value you set (e.g. step of 5: 10, 15, 20, 25...)', 'Admin')}
                            </SettingHint>
                            <FormInput
                                type='number'
                                min={1}
                                value={fixedStep}
                                onChange={(e) => {
                                    setMinAdjustment(null);
                                    setFixedStep(e.target.value);
                                }}
                                onBlur={applyFixedMinAdjustment}
                                $isError={!isFixedStepValid}
                            />
                        </FormGroup>
                    )}

                    {stepType === 'CUSTOM' && (
                        <FormGroup>
                            <FormLabel>{T._('Tier values', 'Admin')}</FormLabel>
                            <SettingHint>{T._('Define specific quantity levels that customers can choose from (e.g. 10, 25, 50, 100).', 'Admin')}</SettingHint>
                            <FormInput
                                type='text'
                                value={customTiers}
                                onChange={(e) => setCustomTiers(e.target.value)}
                                placeholder='10,25,50,100'
                                $isError={!isCustomTiersValid}
                            />
                        </FormGroup>
                    )}
                </SectionCard>
            </StackedBody>
        </>
    );
};

export const MoqRuleEditor: FC<MoqRuleEditorProps> = (props) => {
    return (
        <Suspense
            fallback={
                <>
                    <StackedBreadcrumb>
                        <span onClick={props.onBack}>{T._('Loading...', 'Admin')}</span>
                    </StackedBreadcrumb>
                    <StackedBody>
                        <ContentLoader
                            speed={2}
                            width='100%'
                            height='60'
                            backgroundColor={theme.skeleton.backgroundColor}
                            foregroundColor={theme.skeleton.foregroundColor}
                        >
                            <rect x='0' y='0' rx='8' ry='8' width='100%' height='16' />
                            <rect x='0' y='24' rx='8' ry='8' width='80%' height='16' />
                            <rect x='0' y='48' rx='8' ry='8' width='60%' height='16' />
                        </ContentLoader>
                    </StackedBody>
                </>
            }
        >
            <MoqRuleEditorContent {...props} />
        </Suspense>
    );
};
